import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { MailCheck, LifeBuoy, Phone, MessageCircle, Clock } from "lucide-react";
import { PHONE_DISPLAY, PHONE_TEL, WHATSAPP_URL, isServiceOpen } from "@/contactInfo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useI18n } from "@/i18n";
import { useAuth } from "@/AuthContext";
import api, { apiError } from "@/api";

const C = {
  de: { phone: "Telefon", wa: "WhatsApp", waSub: "Schreib uns direkt", hours: "Servicezeiten", days: "Montag bis Freitag", time: "08:00 – 18:00 Uhr", tz: "Ortszeit Marokko", open: "Jetzt erreichbar", closed: "Gerade geschlossen", form: "Oder schreib uns eine Nachricht" },
  en: { phone: "Phone", wa: "WhatsApp", waSub: "Write to us directly", hours: "Service hours", days: "Monday to Friday", time: "08:00 – 18:00", tz: "Morocco local time", open: "Available now", closed: "Currently closed", form: "Or send us a message" },
  fr: { phone: "Téléphone", wa: "WhatsApp", waSub: "Écrivez-nous directement", hours: "Horaires du service", days: "Du lundi au vendredi", time: "08h00 – 18h00", tz: "Heure locale du Maroc", open: "Joignable maintenant", closed: "Actuellement fermé", form: "Ou envoyez-nous un message" },
  ar: { phone: "الهاتف", wa: "واتساب", waSub: "راسلنا مباشرة", hours: "ساعات الخدمة", days: "من الاثنين إلى الجمعة", time: "08:00 – 18:00", tz: "بتوقيت المغرب", open: "متاحون الآن", closed: "مغلق حالياً", form: "أو أرسل لنا رسالة" },
};

export default function Contact() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const c = C[lang] || C.de;
  const open = isServiceOpen();
  const [form, setForm] = useState({ name: "", email: "", topic: "question", message: "", website: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const v = (k) => (k === "name" ? form.name || (user && user.name) || "" : k === "email" ? form.email || (user && user.email) || "" : form[k]);
  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/contact", { name: v("name"), email: v("email"), topic: form.topic, message: form.message, website: form.website, lang });
      setDone(true);
    } catch (err) {
      toast.error(err.response?.status === 429 ? apiError("TOO_MANY_ATTEMPTS") : t("contact.error"));
    } finally { setBusy(false); }
  };
  return (
    <div className="max-w-3xl mx-auto px-5 sm:px-8 py-14">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
        <div className="text-center mb-8">
          <span className="inline-flex w-12 h-12 rounded-2xl bg-[#C65338] items-center justify-center mb-4"><LifeBuoy className="w-6 h-6 text-white" /></span>
          <h1 className="font-display text-3xl font-black tracking-tight text-[#2D231F]">{t("contact.title")}</h1>
          <p className="mt-2 text-[#8C7A70]">{t("contact.intro")}</p>
        </div>
        <div data-testid="contact-cards" className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
          <a href={`tel:${PHONE_TEL}`} data-testid="contact-phone" className="group rounded-2xl bg-white border border-[#EBDDD3] hover:border-[#C65338] p-5 transition-colors">
            <span className="w-10 h-10 rounded-xl bg-[#C65338]/10 flex items-center justify-center mb-3"><Phone className="w-5 h-5 text-[#C65338]" /></span>
            <div className="text-xs font-bold uppercase tracking-wider text-[#8C7A70]">{c.phone}</div>
            <div className="font-display font-black text-lg text-[#2D231F] mt-1" dir="ltr">{PHONE_DISPLAY}</div>
          </a>
          <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp" className="group rounded-2xl bg-white border border-[#EBDDD3] hover:border-[#25A244] p-5 transition-colors">
            <span className="w-10 h-10 rounded-xl bg-[#25A244]/10 flex items-center justify-center mb-3"><MessageCircle className="w-5 h-5 text-[#25A244]" /></span>
            <div className="text-xs font-bold uppercase tracking-wider text-[#8C7A70]">{c.wa}</div>
            <div className="font-display font-black text-lg text-[#2D231F] mt-1">{c.waSub}</div>
          </a>
          <div data-testid="contact-hours" className="rounded-2xl bg-white border border-[#EBDDD3] p-5">
            <span className="w-10 h-10 rounded-xl bg-[#E69625]/15 flex items-center justify-center mb-3"><Clock className="w-5 h-5 text-[#E69625]" /></span>
            <div className="text-xs font-bold uppercase tracking-wider text-[#8C7A70]">{c.hours}</div>
            <div className="font-semibold text-[#2D231F] mt-1">{c.days}</div>
            <div className="font-display font-black text-lg text-[#2D231F]" dir="ltr">{c.time}</div>
            <div className="text-xs text-[#8C7A70]">{c.tz}</div>
            <div data-testid="contact-open-state" className={`mt-2 inline-flex items-center gap-1.5 text-xs font-bold ${open ? "text-[#3F7A2E]" : "text-[#8C7A70]"}`}>
              <span className={`w-2 h-2 rounded-full ${open ? "bg-[#3F7A2E]" : "bg-[#B5A89F]"}`} />{open ? c.open : c.closed}
            </div>
          </div>
        </div>
        <h2 className="font-display text-xl font-bold text-[#2D231F] text-center mb-4">{c.form}</h2>
        <div className="max-w-xl mx-auto">
        {done ? (
          <div data-testid="contact-done" className="bg-white rounded-2xl border border-[#EBDDD3] p-8 text-center">
            <MailCheck className="w-12 h-12 text-[#5C6646] mx-auto mb-4" />
            <p className="text-[#2D231F]">{t("contact.thanks")}</p>
          </div>
        ) : (
          <form onSubmit={submit} className="bg-white rounded-2xl border border-[#EBDDD3] p-8 space-y-5">
            <div className="space-y-2"><Label>{t("contact.name")}</Label><Input data-testid="contact-name" required maxLength={120} value={v("name")} onChange={set("name")} className="rounded-lg" /></div>
            <div className="space-y-2"><Label>{t("contact.email")}</Label><Input data-testid="contact-email" type="email" required value={v("email")} onChange={set("email")} className="rounded-lg" /></div>
            <div className="space-y-2"><Label>{t("contact.topic")}</Label>
              <select data-testid="contact-topic" value={form.topic} onChange={set("topic")} className="w-full h-10 rounded-lg border border-[#EBDDD3] bg-white px-3 text-sm">
                {["question", "problem", "complaint", "other"].map((k) => <option key={k} value={k}>{t(`contact.t.${k}`)}</option>)}
              </select></div>
            <div className="space-y-2"><Label>{t("contact.message")}</Label><Textarea data-testid="contact-message" required minLength={10} maxLength={3000} rows={6} value={form.message} onChange={set("message")} placeholder={t("contact.messagePh")} className="rounded-lg" /></div>
            <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", height: 0, overflow: "hidden" }}><label>Website<input tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} /></label></div>
            <Button data-testid="contact-send" type="submit" disabled={busy} className="w-full rounded-full bg-[#C65338] hover:bg-[#A93F25] text-white h-11 font-semibold">{t("contact.send")}</Button>
          </form>
        )}
        </div>
      </motion.div>
    </div>
  );
}
